import type { CopilotArtifact } from "@/Contracts/Copilot";
import { zoeStrings } from "@/Copilot/zoeStrings";
import {
	CopilotActionTypes,
	type ArtifactFailureReason,
	type CopilotState,
	type EditorNoticeReason,
	type PendingAssistantTurn,
	type PendingEmailDraft,
	type AppliedArtifactSignal,
	type CopilotActivity,
	type CopilotActivityKind
} from "./interface";

const withoutFailureReason = (
	reasons: Record<string, ArtifactFailureReason>,
	artifactId: string
): Record<string, ArtifactFailureReason> => {
	if (!(artifactId in reasons)) return reasons;
	const next = { ...reasons };
	delete next[artifactId];
	return next;
};

export type {
	ArtifactFailureReason,
	CopilotState,
	EditorNoticeReason,
	PendingAssistantTurn,
	PendingEmailDraft,
	AppliedArtifactSignal,
	CopilotActivity,
	CopilotActivityKind
} from "./interface";

const THINKING_ACTIVITY: CopilotActivity = {
	label: zoeStrings.thinking.default,
	kind: "thinking"
};

const EMAIL_THREAD_MESSAGE_DRAWER_CLOSED = "EMAIL_THREAD_MESSAGE_DRAWER_CLOSED";

export const initialCopilotState: CopilotState = {
	isOpen: false,
	session: null,
	messages: [],
	pending: null,
	appliedArtifacts: {},
	artifactFailureReasons: {},
	error: null,
	editorNotice: null,
	pendingEmailDraft: null,
	pendingEmailDraftToken: 0,
	lastAppliedArtifact: null,
	pricingScopeBlockId: null
};

type Action = { type: string; payload?: unknown };

export const copilotReducer = (
	state: CopilotState = initialCopilotState,
	action: Action
): CopilotState => {
	switch (action.type) {
		case CopilotActionTypes.OPEN:
			return { ...state, isOpen: true };
		case CopilotActionTypes.CLOSE:
			return { ...state, isOpen: false };
		case CopilotActionTypes.SET_SESSION: {
			const payload = action.payload as {
				session: CopilotState["session"];
			};
			return { ...state, session: payload.session };
		}
		case CopilotActionTypes.CLEAR_SESSION:
			return { ...state, session: null };
		case CopilotActionTypes.START_USER_TURN: {
			const p = action.payload as { id: string; text: string };
			return {
				...state,
				messages: [
					...state.messages,
					{
						role: "user",
						id: p.id,
						text: p.text,
						createdAt: Date.now()
					}
				],
				error: null
			};
		}
		case CopilotActionTypes.START_PENDING_TURN: {
			const p = action.payload as { id: string };
			const pending: PendingAssistantTurn = {
				id: p.id,
				text: "",
				artifacts: [],
				toolCalls: [],
				activity: THINKING_ACTIVITY
			};
			return { ...state, pending };
		}
		case CopilotActionTypes.APPEND_TOKEN: {
			if (!state.pending) return state;
			const p = action.payload as { delta: string };
			const toolInFlight = state.pending.toolCalls.some(
				tc => tc.finishedAt === undefined
			);
			const activity: CopilotActivity =
				!toolInFlight && state.pending.activity.kind !== "writing"
					? { label: zoeStrings.thinking.writing, kind: "writing" }
					: state.pending.activity;
			return {
				...state,
				pending: {
					...state.pending,
					text: state.pending.text + p.delta,
					activity
				}
			};
		}
		case CopilotActionTypes.APPEND_STATUS: {
			if (!state.pending) return state;
			const p = action.payload as {
				id: string;
				label: string;
				kind: CopilotActivityKind | "done";
			};
			if (p.kind === "done") return state;
			return {
				...state,
				pending: {
					...state.pending,
					activity: { label: p.label, kind: p.kind }
				}
			};
		}
		case CopilotActionTypes.APPEND_TOOL_START: {
			if (!state.pending) return state;
			const p = action.payload as { tools: Array<{ name: string }> };
			return {
				...state,
				pending: {
					...state.pending,
					toolCalls: [
						...state.pending.toolCalls,
						...p.tools.map(t => ({
							tool: t.name,
							startedAt: Date.now()
						}))
					]
				}
			};
		}
		case CopilotActionTypes.APPEND_TOOL_END: {
			if (!state.pending) return state;
			const p = action.payload as {
				results: Array<{ name: string; ok: boolean }>;
			};
			const toolCalls = [...state.pending.toolCalls];
			let resultIdx = 0;
			const updated = toolCalls.map(tc => {
				if (
					tc.finishedAt === undefined &&
					resultIdx < p.results.length
				) {
					const r = p.results[resultIdx];
					resultIdx += 1;
					return { ...tc, finishedAt: Date.now(), ok: r.ok };
				}
				return tc;
			});
			const stillRunning = updated.some(
				tc => tc.finishedAt === undefined
			);
			return {
				...state,
				pending: {
					...state.pending,
					toolCalls: updated,
					activity: stillRunning
						? state.pending.activity
						: THINKING_ACTIVITY
				}
			};
		}
		case CopilotActionTypes.APPEND_ARTIFACT: {
			if (!state.pending) return state;
			const p = action.payload as { artifact: CopilotArtifact };
			return {
				...state,
				pending: {
					...state.pending,
					artifacts: [...state.pending.artifacts, p.artifact]
				}
			};
		}
		case CopilotActionTypes.COMMIT_PENDING_TURN: {
			if (!state.pending) return state;
			const committed = {
				role: "assistant" as const,
				id: state.pending.id,
				text: state.pending.text,
				artifacts: state.pending.artifacts,
				toolCalls: state.pending.toolCalls,
				createdAt: Date.now()
			};
			return {
				...state,
				pending: null,
				messages: [...state.messages, committed]
			};
		}
		case CopilotActionTypes.SET_ERROR: {
			const p = action.payload as CopilotState["error"];
			return { ...state, error: p };
		}
		case CopilotActionTypes.MARK_ARTIFACT_APPLIED: {
			const p = action.payload as { artifactId: string };
			return {
				...state,
				appliedArtifacts: {
					...state.appliedArtifacts,
					[p.artifactId]: "applied"
				},
				artifactFailureReasons: withoutFailureReason(
					state.artifactFailureReasons,
					p.artifactId
				)
			};
		}
		case CopilotActionTypes.MARK_ARTIFACT_FAILED: {
			const p = action.payload as {
				artifactId: string;
				reason?: ArtifactFailureReason;
			};
			return {
				...state,
				appliedArtifacts: {
					...state.appliedArtifacts,
					[p.artifactId]: "failed"
				},
				artifactFailureReasons: p.reason
					? {
							...state.artifactFailureReasons,
							[p.artifactId]: p.reason
					  }
					: withoutFailureReason(
							state.artifactFailureReasons,
							p.artifactId
					  )
			};
		}
		case CopilotActionTypes.MARK_ARTIFACT_DISCARDED: {
			const p = action.payload as { artifactId: string };
			return {
				...state,
				appliedArtifacts: {
					...state.appliedArtifacts,
					[p.artifactId]: "discarded"
				},
				artifactFailureReasons: withoutFailureReason(
					state.artifactFailureReasons,
					p.artifactId
				)
			};
		}
		case CopilotActionTypes.RESET_CONVERSATION:
			return {
				...initialCopilotState,
				isOpen: state.isOpen,
				pendingEmailDraft: state.pendingEmailDraft,
				pendingEmailDraftToken: state.pendingEmailDraftToken
			};
		case CopilotActionTypes.SET_PENDING_EMAIL_DRAFT: {
			const p = action.payload as PendingEmailDraft;
			return {
				...state,
				pendingEmailDraft: p,
				pendingEmailDraftToken: state.pendingEmailDraftToken + 1
			};
		}
		case CopilotActionTypes.CLEAR_PENDING_EMAIL_DRAFT:
			return { ...state, pendingEmailDraft: null };
		case EMAIL_THREAD_MESSAGE_DRAWER_CLOSED:
			return state.pendingEmailDraft === null
				? state
				: { ...state, pendingEmailDraft: null };
		case CopilotActionTypes.SIGNAL_ARTIFACT_APPLIED: {
			const p = action.payload as AppliedArtifactSignal;
			return { ...state, lastAppliedArtifact: p };
		}
		case CopilotActionTypes.SET_PRICING_SCOPE: {
			const p = action.payload as { blockId: string };
			return { ...state, pricingScopeBlockId: p.blockId };
		}
		case CopilotActionTypes.CLEAR_PRICING_SCOPE:
			return { ...state, pricingScopeBlockId: null };
		case CopilotActionTypes.SET_EDITOR_NOTICE: {
			const p = action.payload as { reason: EditorNoticeReason };
			return { ...state, editorNotice: p.reason };
		}
		case CopilotActionTypes.CLEAR_EDITOR_NOTICE:
			return state.editorNotice === null
				? state
				: { ...state, editorNotice: null };
		default:
			return state;
	}
};

export default copilotReducer;
